/**
 * Properties page. Built to match `design/Syntra/properties.html`:
 *
 *   - Page header (title + subtitle).
 *   - 4-tile KPI row (Properties · Portfolio Value · Monthly Cash Flow · Avg Cap Rate).
 *   - Auto-fill grid of property cards (one per property moved to rehab).
 *   - Empty state directing the user back through the lifecycle.
 *
 * Data source is `useOwnedProperties`, which batch-fetches the three tables
 * we need (rehab_projects, pipeline_properties, property_setting). Per-card
 * metrics come from the pure `property-metrics` calculator.
 *
 * The legacy portfolio dashboard (analytics, P&L, edit dialog, send-to-rehab
 * icon) is intentionally dropped — the design treats Properties as a focused
 * performance view, not an admin surface.
 */
import { useMemo, useState } from "react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Building2, Plus } from "lucide-react";

import { AddPropertyDialog } from "@/components/properties/add-property/AddPropertyDialog";
import { PropertyCard } from "@/components/properties/PropertyCard";
import { PropertiesKpiRow } from "@/components/properties/PropertiesKpiRow";
import {
  PropertiesPerformanceChart,
  type PerformanceEntry,
} from "@/components/properties/PropertiesPerformanceChart";
import {
  aggregateKpis,
  computePropertyMetrics,
  type PropertyMetrics,
} from "@/components/properties/property-metrics";
import {
  buildPropertyMonths,
  normalizeFromSettings,
} from "@/components/properties/cashflow-metrics";
import { overlayActualRentMonthly } from "@/components/properties/cashflow-detail";
import { PropertyDetailView } from "@/components/properties/detail/PropertyDetailView";
import { RentalListingBlockedDialog } from "@/components/rentals/RentalListingBlockedDialog";
import { useMoveToRehab } from "@/hooks/use-move-to-rehab";
import { useOwnedProperties, type OwnedProperty } from "@/hooks/use-owned-properties";
import { usePropertyLogs } from "@/hooks/use-property-logs";
import { useWorkspace } from "@/hooks/use-workspace";
import { RentalListingBlockedError } from "@/lib/rental-listing-guard";

export default function Properties() {
  const { items, isLoading, rentByMonthByProperty } = useOwnedProperties();
  const { logsByProperty } = usePropertyLogs();
  const { canDo } = useWorkspace();
  const moveToRehab = useMoveToRehab();
  const [selectedItem, setSelectedItem] = useState<OwnedProperty | null>(null);
  const [rentalBlocked, setRentalBlocked] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const canAdd = canDo("pipeline.create");

  // Enroll a property into rehab — same action as the pipeline's "Move to
  // Rehab". A property that's actively listed for rent is blocked with a modal.
  const handleMoveToRehab = (id: string) => {
    moveToRehab.mutate(id, {
      onError: (err) => {
        if (err instanceof RentalListingBlockedError) setRentalBlocked(true);
      },
    });
  };

  // Compute metrics once per item — reused for both the card and the KPI row.
  const decorated = useMemo(
    () =>
      items.map((item) => {
        const metrics: PropertyMetrics = computePropertyMetrics({
          settings: item.settings,
          fallbackPurchase: item.purchasePrice ?? 0,
          strategy: item.strategy,
          rehabStartDate: item.rehabStartDate,
          ownedDate: item.ownedDate,
          actualMonthlyRent: item.actualMonthlyRent,
          loan: item.loan,
          rehabLineItems: item.rehabLineItems,
        });
        return { item, metrics };
      }),
    [items],
  );

  const kpis = useMemo(
    () =>
      aggregateKpis(
        decorated.map(({ item, metrics }) => ({
          metrics,
          status: item.status,
        })),
      ),
    [decorated],
  );

  // Series for the "Monthly performance" chart (active properties only). Past
  // months come from frozen property_log snapshots; the current month is
  // computed live; future months are never produced.
  const perfEntries = useMemo<PerformanceEntry[]>(
    () =>
      decorated
        .filter(({ item }) => item.status !== "sold")
        .map(({ item, metrics }) => {
          const strategy = item.strategy ?? "BRRRR";
          const live = normalizeFromSettings({
            strategy,
            settings: item.settings,
            loan: item.loan,
            rentAmount: null,
          });
          const months = buildPropertyMonths({
            strategy,
            logs: logsByProperty.get(item.id) ?? [],
            live,
          });
          // Strict-actual rent: overlay real rent_payments for every property.
          // Months with no payment (or a property with none at all) read $0
          // income — never the projected monthly_rent.
          const rentByMonth = rentByMonthByProperty.get(item.id) ?? new Map();
          return {
            id: item.id,
            label: item.property.address ?? "Property",
            strategy: metrics.strategy,
            months: overlayActualRentMonthly(months, rentByMonth),
          };
        }),
    [decorated, logsByProperty, rentByMonthByProperty],
  );

  // Property detail swaps the page contents in-place (matches /pipeline + /find).
  if (selectedItem) {
    return (
      <div className="flex h-[calc(100vh-4rem)] flex-col overflow-y-auto">
        <PropertyDetailView item={selectedItem} onBack={() => setSelectedItem(null)} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">
            Properties
          </h1>
          <p className="text-sm text-muted-foreground">
            Track the performance of every property you've purchased.
          </p>
        </div>
        {canAdd && (
          <Button onClick={() => setAddOpen(true)}>
            <Plus className="mr-1 h-4 w-4" /> Add property
          </Button>
        )}
      </div>

      <PropertiesKpiRow kpis={kpis} />

      {isLoading ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-[460px] w-full rounded-lg" />
          ))}
        </div>
      ) : decorated.length === 0 ? (
        <Card className="shadow-soft">
          <CardContent className="py-20 text-center">
            <Building2 className="mx-auto mb-3 h-12 w-12 text-muted-foreground/40" />
            <h3 className="text-base font-bold text-foreground">
              No properties to show
            </h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Properties appear here once you click
              <span className="font-semibold text-primary"> Move to Rehab</span>{" "}
              on a Closed Won deal in the Pipeline.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {decorated.map(({ item, metrics }) => {
            const pd = item.property;
            const addr1 = pd.address ?? "Property";
            const cityState = [pd.city, pd.state].filter(Boolean).join(", ");
            const specs = [
              pd.bedrooms ? `${pd.bedrooms} bd` : "",
              pd.bathrooms ? `${pd.bathrooms} ba` : "",
              pd.sqft ? `${pd.sqft.toLocaleString()} sf` : "",
            ]
              .filter(Boolean)
              .join(" · ");
            const addr2 = [cityState, specs].filter(Boolean).join(" · ");
            return (
              <PropertyCard
                key={item.id}
                id={item.id}
                imgUrl={pd.imageUrl ?? null}
                addr1={addr1}
                addr2={addr2}
                status={item.status}
                strategy={item.strategy}
                tiles={metrics.tiles}
                daysOwned={metrics.daysOwned}
                onOpen={() => setSelectedItem(item)}
                canMoveToRehab={!item.hasRehabProject && item.status !== "sold"}
                onMoveToRehab={handleMoveToRehab}
                isMovingToRehab={
                  moveToRehab.isPending && moveToRehab.variables === item.id
                }
              />
            );
          })}
        </div>
      )}

      {!isLoading && perfEntries.length > 0 && (
        <PropertiesPerformanceChart entries={perfEntries} />
      )}

      {/* Blocks "Move to Rehab" while the property is actively listed for rent. */}
      <RentalListingBlockedDialog
        open={rentalBlocked}
        onOpenChange={setRentalBlocked}
      />

      <AddPropertyDialog open={addOpen} onOpenChange={setAddOpen} />
    </div>
  );
}
